import client from "@/api/openapi-ts";
import { fetchExportDownload } from "@/lib/exports/downloadFile";
import { defineStore, acceptHMRUpdate } from "pinia";
import { ref, computed } from "vue";
import { usePlugins } from "./helpers/usePlugins";
import { useExports } from "./helpers/useExports";
import { useUserStore } from "@/stores/UserStore";
import { usePermissions } from "@/composables/usePermissions";
import { featureFlags } from "@/lib/feature_flags/flags";
import type { components } from "@/api/schema";
import {
  Category,
  Creative,
  Dates,
  Race,
  Sponsor,
  BASE_MEDIUM_TYPES,
  CTV_MEDIUM_TYPES,
} from "@/stores/ReachStore";
import type { Geography } from "@/types/api/Geography";

type schemas = components["schemas"];

/**
 * The `reach_frequency/*` endpoints used below (dates, categories, races,
 * sponsors, network_stations, creatives) are plain Flask routes without
 * flask-smorest schemas, so they are untyped in the generated OpenAPI client.
 * Their request bodies/responses are cast at the call sites until the backend
 * gains proper schemas.
 */
type PendingTaskResponse = {
  task_id: string;
};

type Filters = {
  meta_audience_id?: number;
  date_ranges?: string[];
  category?: Category;
  races?: string;
  advertiser_parties?: string[];
  geographies?: Geography[];
  ad_types?: string[];
  medium_types?: string[];
  sponsors?: Sponsor[];
  creatives?: Creative[];
  network_stations?: string[];
  creative_ids?: number[];
};

type Loading = {
  dateRange: boolean;
  categories: boolean;
  races: boolean;
  sponsors: boolean;
  networkStations: boolean;
  creatives: boolean;
  overview: boolean;
  creativesBreakdown: boolean;
  dailyStats: boolean;
};

type Errors = {
  overview: string | undefined;
  creativesBreakdown: string | undefined;
  dailyStats: string | undefined;
};

type TaskIds = {
  dateRange: string | undefined;
  categories: string | undefined;
  races: string | undefined;
  sponsors: string | undefined;
  networkStations: string | undefined;
  creatives: string | undefined;
  overview: string | undefined;
  creativesBreakdown: string | undefined;
  dailyStats: string | undefined;
};

type SecondaryFilters = {
  markets?: number[];
  mediums?: string[];
  sponsors?: number[];
  advertiser_parties?: string[];
};

export const useAdExplorerStore = defineStore("adExplorer", () => {
  const userStore = useUserStore();
  const { hasRole } = usePermissions();

  const loading = ref<Loading>({
    dateRange: false,
    categories: false,
    races: false,
    sponsors: false,
    networkStations: false,
    creatives: false,
    overview: false,
    creativesBreakdown: false,
    dailyStats: false,
  });
  const errors = ref<Errors>({
    overview: undefined,
    creativesBreakdown: undefined,
    dailyStats: undefined,
  });
  const taskIds = ref<TaskIds>({
    dateRange: undefined,
    categories: undefined,
    races: undefined,
    sponsors: undefined,
    networkStations: undefined,
    creatives: undefined,
    overview: undefined,
    creativesBreakdown: undefined,
    dailyStats: undefined,
  });
  const dates = ref<Dates | undefined>(undefined);
  const filters = ref<Filters>({
    medium_types: [],
    ad_types: [],
  });

  const ctvDataEnabled = ref(false);
  let ctvDataFlagPromise: Promise<void> | undefined;

  const defaultMediumTypes = computed(() =>
    ctvDataEnabled.value ? [...CTV_MEDIUM_TYPES] : [...BASE_MEDIUM_TYPES],
  );

  const effectiveMediumTypes = computed(() =>
    filters.value.medium_types?.length
      ? filters.value.medium_types
      : defaultMediumTypes.value,
  );

  async function initCtvDataFlag() {
    if (!ctvDataFlagPromise) {
      ctvDataFlagPromise = featureFlags
        .flagIsEnabled("use_ctv_data", {
          email: userStore.currentUser?.email,
          admin: hasRole("admin"),
        })
        .then((enabled) => {
          ctvDataEnabled.value = enabled;
        });
    }
    return ctvDataFlagPromise;
  }

  const categories = ref<Category[]>([]);
  const categoriesSorted = computed(() => {
    return [...categories.value].sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
  });
  const races = ref<Race[]>([]);
  const sponsors = ref<Sponsor[]>([]);
  const networkStations = ref<string[]>([]);
  const creatives = ref<Creative[]>([]);
  const overview =
    ref<components["schemas"]["AdExplorerGetOverviewResponse"]>();
  /** WebSocket sets `payload.data.creatives` (array), not the full API envelope. */
  const creativesBreakdown =
    ref<components["schemas"]["AdExplorerGetCreativesBreakdownCreative"][]>();
  const savedCreatives = ref<
    components["schemas"]["AdExplorerGetCreativesBreakdownCreative"][]
  >([]);
  const dailyStats =
    ref<components["schemas"]["AdExplorerGetLineChartResponse"]>();
  const appliedSecondaryFilters = ref<SecondaryFilters>();
  const rawDataExportError = ref<string | undefined>();

  const { $ws } = usePlugins();

  // The shared export manager owns the raw-data export's loading/task state and
  // its download (including abort-on-reset). Errors are surfaced through the
  // store's existing `rawDataExportError` ref so the page's error UI is unchanged.
  const exportManager = useExports<"rawData">({
    channel: "AdExplorer",
    keys: ["rawData"],
    errorMessage: () => "Error creating raw data export",
    onError: (_key, message) => {
      rawDataExportError.value = message;
    },
    fetchFile: (file, signal) =>
      fetchExportDownload(
        "/api/v1/ad_explorer/export/{filename}",
        file,
        signal,
      ),
  });

  const baseParams = (secondaryFilters?: SecondaryFilters) => {
    const dates = filters.value.date_ranges!;
    let geographies = filters.value.geographies;
    if (secondaryFilters?.markets) {
      geographies = geographies?.filter((g) =>
        secondaryFilters.markets?.includes(g.ref_id),
      );
    }
    let mediums = effectiveMediumTypes.value;
    if (secondaryFilters?.mediums) {
      mediums = secondaryFilters.mediums;
    }
    let sponsors = filters.value.sponsors?.map((s) => s.id) || [];
    if (secondaryFilters?.sponsors) {
      sponsors = secondaryFilters.sponsors;
    }
    // Party filters only apply to Political; ignore stale secondary parties
    // after the primary category has changed away from Political.
    let advertiser_parties: string[] = [];
    if (filters.value.category?.name === "Political") {
      advertiser_parties = filters.value.advertiser_parties
        ? filters.value.advertiser_parties
        : [];
      if (secondaryFilters?.advertiser_parties) {
        advertiser_parties = secondaryFilters.advertiser_parties;
      }
    }
    return {
      meta_audience_id: filters.value.meta_audience_id,
      start_date: dates[0],
      end_date: dates[1],
      geographies: geographies,
      category_ids: filters.value.category?.id
        ? [filters.value.category?.id]
        : [],
      creative_ids: filters.value.creative_ids || [],
      races: filters.value.races ? [filters.value.races] : [],
      advertiser_parties,
      sponsor_ids: sponsors,
      ad_types: filters.value.ad_types || [],
      medium_types: mediums,
      network_stations: filters.value.network_stations || [],
    };
  };

  function $reset() {
    exportManager.cancel();
    loading.value = {
      dateRange: false,
      categories: false,
      races: false,
      sponsors: false,
      networkStations: false,
      creatives: false,
      overview: false,
      creativesBreakdown: false,
      dailyStats: false,
    };
    errors.value = {
      overview: undefined,
      creativesBreakdown: undefined,
      dailyStats: undefined,
    };
    taskIds.value = {
      dateRange: undefined,
      categories: undefined,
      races: undefined,
      sponsors: undefined,
      networkStations: undefined,
      creatives: undefined,
      overview: undefined,
      creativesBreakdown: undefined,
      dailyStats: undefined,
    };
    filters.value = { medium_types: [], ad_types: [] };
    races.value = [];
    sponsors.value = [];
    networkStations.value = [];
    creatives.value = [];
    overview.value = undefined;
    creativesBreakdown.value = undefined;
    savedCreatives.value = [];
    dailyStats.value = undefined;
    appliedSecondaryFilters.value = undefined;
    rawDataExportError.value = undefined;
  }

  return {
    loading,
    errors,
    taskIds,
    filters,
    dates,
    categories,
    categoriesSorted,
    races,
    sponsors,
    creatives,
    networkStations,
    overview,
    creativesBreakdown,
    savedCreatives,
    dailyStats,
    appliedSecondaryFilters,
    rawDataExportError,
    ctvDataEnabled,
    defaultMediumTypes,
    effectiveMediumTypes,
    initCtvDataFlag,
    exportLoading: exportManager.loading,
    handleExportMessage: exportManager.handleMessage,
    $reset,
    async getDateRange() {
      loading.value.dateRange = true;
      try {
        const { data } = await client.GET("/api/v1/reach_frequency/dates");
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.dateRange = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.dateRange = false;
        }
      } catch {
        loading.value.dateRange = false;
      }
    },
    async getCategories() {
      loading.value.categories = true;
      try {
        const { data, error } = await client.GET(
          "/api/v1/reach_frequency/categories",
        );
        if (error) {
          throw error;
        }
        categories.value = (data as Category[] | undefined) ?? [];
      } finally {
        loading.value.categories = false;
      }
    },
    async getRaces() {
      loading.value.races = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST("/api/v1/reach_frequency/races", {
          body: {
            start_date: dates[0],
            end_date: dates[1],
            geographies: filters.value.geographies,
            advertiser_parties: filters.value.advertiser_parties
              ? filters.value.advertiser_parties
              : [],
          } as never,
        });
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.races = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.races = false;
        }
      } catch {
        loading.value.races = false;
      }
    },
    async getSponsors() {
      const metaAudienceId = filters.value.meta_audience_id;
      if (typeof metaAudienceId !== "number") {
        return;
      }
      loading.value.sponsors = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST("/api/v1/reach_frequency/sponsors", {
          body: {
            start_date: dates[0],
            end_date: dates[1],
            meta_audience_id: metaAudienceId,
            geographies: filters.value.geographies,
            category_ids: filters.value.category?.id
              ? [filters.value.category?.id]
              : [],
            races: filters.value.races ? [filters.value.races] : [],
            advertiser_parties: filters.value.advertiser_parties
              ? filters.value.advertiser_parties
              : [],
            ad_types: filters.value.ad_types || [],
            medium_types: effectiveMediumTypes.value,
          } as schemas["ReachFrequencyListSponsorsRequest"],
        });
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.sponsors = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.sponsors = false;
        }
      } catch {
        loading.value.sponsors = false;
      }
    },
    async getNetworkStations() {
      loading.value.networkStations = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/network_stations",
          {
            body: {
              start_date: dates[0],
              end_date: dates[1],
              geographies: filters.value.geographies,
              category_ids: filters.value.category?.id
                ? [filters.value.category?.id]
                : [],
              races: filters.value.races ? [filters.value.races] : [],
              advertiser_parties: filters.value.advertiser_parties
                ? filters.value.advertiser_parties
                : [],
              ad_types: filters.value.ad_types || [],
              medium_types: effectiveMediumTypes.value,
              sponsor_ids: filters.value.sponsors?.map((s) => s.id) || [],
            } as never,
          },
        );
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.networkStations = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.networkStations = false;
        }
      } catch {
        loading.value.networkStations = false;
      }
    },
    async getCreatives() {
      loading.value.creatives = true;
      const dates = filters.value.date_ranges!;
      try {
        const { data } = await client.POST(
          "/api/v1/reach_frequency/creatives",
          {
            body: {
              start_date: dates[0],
              end_date: dates[1],
              geographies: filters.value.geographies,
              category_ids: filters.value.category?.id
                ? [filters.value.category?.id]
                : [],
              races: filters.value.races ? [filters.value.races] : [],
              advertiser_parties: filters.value.advertiser_parties
                ? filters.value.advertiser_parties
                : [],
              sponsor_ids: filters.value.sponsors?.map((s) => s.id) || [],
              ad_types: filters.value.ad_types || [],
              medium_types: effectiveMediumTypes.value,
              network_stations: filters.value.network_stations,
            } as never,
          },
        );
        const taskId = (data as PendingTaskResponse | undefined)?.task_id;
        if (taskId) {
          taskIds.value.creatives = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.creatives = false;
        }
      } catch {
        loading.value.creatives = false;
      }
    },
    async getOverview(secondaryFilters?: SecondaryFilters) {
      loading.value.overview = true;
      errors.value.overview = undefined;
      overview.value = undefined;
      const params = baseParams(
        secondaryFilters ?? appliedSecondaryFilters.value,
      );
      try {
        const { data } = await client.POST("/api/v1/ad_explorer/overview", {
          body: params as schemas["AdExplorerGetOverviewRequest"],
        });
        const taskId = data?.task_id;
        if (taskId) {
          taskIds.value.overview = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.overview = false;
        }
      } catch {
        loading.value.overview = false;
      }
    },
    async getCreativesBreakdown(secondaryFilters?: SecondaryFilters) {
      loading.value.creativesBreakdown = true;
      errors.value.creativesBreakdown = undefined;
      creativesBreakdown.value = undefined;
      const params = baseParams(
        secondaryFilters ?? appliedSecondaryFilters.value,
      );
      try {
        const { data } = await client.POST(
          "/api/v1/ad_explorer/creatives_breakdown",
          {
            body: params as schemas["AdExplorerGetCreativesBreakdownRequest"],
          },
        );
        const taskId = data?.task_id;
        if (taskId) {
          taskIds.value.creativesBreakdown = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.creativesBreakdown = false;
        }
      } catch {
        loading.value.creativesBreakdown = false;
      }
    },
    async getDailyStats(secondaryFilters?: SecondaryFilters) {
      loading.value.dailyStats = true;
      errors.value.dailyStats = undefined;
      dailyStats.value = undefined;
      const params = baseParams(
        secondaryFilters ?? appliedSecondaryFilters.value,
      );
      try {
        const { data } = await client.POST("/api/v1/ad_explorer/line_chart", {
          body: params as schemas["AdExplorerGetLineChartRequest"],
        });
        const taskId = data?.task_id;
        if (taskId) {
          taskIds.value.dailyStats = taskId;
          $ws?.subscribe({ channel: "AdExplorer", payload: { id: taskId } });
        } else {
          loading.value.dailyStats = false;
        }
      } catch {
        loading.value.dailyStats = false;
      }
    },
    createRawDataExport(secondaryFilters?: SecondaryFilters) {
      // The shared export manager only resets its own internal `errors` record;
      // a previous failure surfaced through `rawDataExportError` would otherwise
      // stay visible while the new export runs, so clear it here.
      rawDataExportError.value = undefined;
      const params = baseParams(
        secondaryFilters ?? appliedSecondaryFilters.value,
      );
      return exportManager.startExport("rawData", () =>
        client.POST("/api/v1/ad_explorer/export/raw_data", {
          body: {
            ...params,
            sync: false,
          } as schemas["AdExplorerRawDataExportRequest"],
        }),
      );
    },
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAdExplorerStore, import.meta.hot));
}
